import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApi, useCaller } from '@/hooks/api.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import { useDebounce } from '@/hooks/debounce.hook';
import { generateUrlParams } from '@/utils/common';

export type VirtualAccountOption = {
  label: string;
  value: string;
  accountNumber?: string;
  accountName?: string;
  balance?: {
    amount: number;
    currency: string;
  };
};

type PaginationType = {
  Page: number;
  totalPages: number;
  totalElements: number;
  Size: number;
};

type UseVirtualAccountsConfig = {
  enabled?: boolean;
};

export const useVirtualAccounts = (config: UseVirtualAccountsConfig = {}) => {
  const { enabled = true } = config;
  const api = useApi('');
  const caller = useCaller();
  const customerId = useRootSelector((state) => state.auth.customerId);

  // Local state for dropdown - independent from Redux
  const [localData, setLocalData] = useState<any[]>([]);
  const [fullData, setFullData] = useState<any[]>([]); // Maintain full list for selected account lookup
  const [pagination, setPagination] = useState<PaginationType>({
    Page: 1,
    totalPages: 1,
    totalElements: 0,
    Size: 5,
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const hasLoadedInitialData = useRef(false);

  // Fetch data directly from API without using Redux
  const fetchVirtualAccounts = useCallback(
    async (page: number, size: number, append = false, search?: string) => {
      if (!customerId) return;

      setLoading(true);

      const queryParams: { [key: string]: string | undefined } = {
        Page: page.toString(),
        Size: size.toString(),
        Search: search,
      };
      const urlParams = generateUrlParams(queryParams);

      const {
        content,
        succeeded,
        page: responsePage,
        size: responseSize,
        totalPages,
        totalElements,
      } = await caller(
        () => api.get(`/v2/api/customer/${customerId}/virtual-accounts?${urlParams}`),
        {
          loadingKey: 'get-virtual-account-dropdown',
        },
      );

      if (succeeded) {
        setLocalData((prev) => (append ? [...prev, ...content] : content));

        // Update fullData only when NOT searching (loading full list)
        if (!search) {
          setFullData((prev) => (append ? [...prev, ...content] : content));
        }

        setPagination({
          Page: responsePage,
          totalPages,
          totalElements,
          Size: responseSize,
        });
      }

      setLoading(false);
    },
    [caller, api, customerId],
  );

  // Convert VirtualAccount[] to VirtualAccountOption[]
  // All accounts from full list (for selected account lookup)
  const allVirtualAccounts = useMemo<VirtualAccountOption[]>(() => {
    return fullData.map((va) => ({
      label: va.nickName || va.accountName || va.accountNumber || 'Unknown Account',
      value: va.id,
      accountNumber: va.accountNumber,
      accountName: va.accountName,
      balance: va.balance,
    }));
  }, [fullData]);

  // Visible accounts from current data (search results or full list)
  const visibleVirtualAccounts = useMemo<VirtualAccountOption[]>(() => {
    return localData.map((va) => ({
      label: va.nickName || va.accountName || va.accountNumber || 'Unknown Account',
      value: va.id,
      accountNumber: va.accountNumber,
      accountName: va.accountName,
      balance: va.balance,
    }));
  }, [localData]);

  // Check if there are more items to load from API
  const hasMore = pagination.Page < pagination.totalPages;

  // Load more items from API
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    const nextPage = pagination.Page + 1;
    await fetchVirtualAccounts(nextPage, 10, true, debouncedSearchTerm || undefined);
  }, [loading, hasMore, pagination.Page, fetchVirtualAccounts, debouncedSearchTerm]);

  // Search handler
  const handleSearch = useCallback((search: string) => {
    setSearchTerm(search);
  }, []);

  // Initial load - load first 100 items only once on mount
  useEffect(() => {
    if (enabled && !hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      fetchVirtualAccounts(1, 10, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Track previous search term to detect when user clears search
  const prevSearchRef = useRef<string | undefined>(undefined);

  // Re-fetch when search term changes
  useEffect(() => {
    if (!enabled) return;

    // Trigger API call if:
    // 1. User is searching (has search term), OR
    // 2. User just cleared search (prev had value, current is empty)
    if (debouncedSearchTerm || prevSearchRef.current) {
      fetchVirtualAccounts(1, 10, false, debouncedSearchTerm || undefined);
    }
    prevSearchRef.current = debouncedSearchTerm;
  }, [enabled, debouncedSearchTerm, fetchVirtualAccounts]);

  return {
    visibleVirtualAccounts,
    allVirtualAccounts,
    isLoading: loading,
    handleSearch,
    loadMore,
    hasMore,
  };
};
